import { DialogTitle, Stack, Typography, Box, DialogContent, DialogContentText, DialogActions, Button, TextField, Dialog, IconButton, Alert, CircularProgress, InputAdornment, Paper, Tooltip } from '@mui/material';
import { IconTimeDuration45, IconX } from '@tabler/icons-react';
import { useEffect, useState, useRef } from 'react';
import icon from 'src/assets/images/logos/logo-sm.svg';
import { apiConfig, getApiUrl } from 'src/config/apiConfig';
import axiosIn from 'src/middlewares/axiosInterceptor';
import { setSecuseekGenerateApiKeyDialog, setToastMessage } from 'src/store/my_pages/app';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { dispatch, useSelector } from 'src/store/Store';
import { extractErrorMsg } from 'src/utils/utils';
import { TfiTime } from 'react-icons/tfi';
/* 
step 0: Checking your API Key
step 1: Generate SecuSeek API Key
step 2: Your API Key is Ready!
*/

const SecuseekGenerateApiKeyDialog = ({ step = 0 }: { step?: number }) => {
  const { secuseekGenerateApiKeyDialog } = useSelector((state: any) => state.appGeneralReducer);
  const [loading, setLoading] = useState(false);
  const [step_, setStep] = useState(step);
  const isBlankPage = secuseekGenerateApiKeyDialog?.cannotClose && secuseekGenerateApiKeyDialog?.show;

  const [apikey_name, setapikey_name] = useState('');
  const [generatedApiKey, setGeneratedApiKey] = useState('');
  const [existingApiKey, setExistingApiKey] = useState<any>(secuseekGenerateApiKeyDialog?.existingData || null);
  const prevShowRef = useRef<boolean | undefined>();

  const handleGetExistingApiKey = async () => {
    setLoading(true);
    try {
      const curl = getApiUrl(apiConfig.api_key_getpost);
      const res: any = await axiosIn.get(curl);
      if (res.status === 200 && res.data) {
        setExistingApiKey(res.data);
        setGeneratedApiKey(res.data.apiKey);
        // Update Redux state with the existing API key data
        dispatch(setSecuseekGenerateApiKeyDialog({ existingData: res.data }));
        setStep(2);
      } else {
        // Clear existingData if no API key exists
        dispatch(setSecuseekGenerateApiKeyDialog({ existingData: null }));
        setStep(1);
      }
    } catch (error) {
      // Clear existingData if fetch fails
      dispatch(setSecuseekGenerateApiKeyDialog({ existingData: null }));
      setStep(1);
      //   const msg = extractErrorMsg(error);
      //   dispatch(setToastMessage({ msg: msg || 'You don\'t have any API Key!', color: 'error' }));
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateApiKey = async () => {
    setLoading(true);
    try {
      const curl = getApiUrl(apiConfig.api_key_getpost);
      const res: any = await axiosIn.post(curl, { name: apikey_name });
      if (res.status === 200) {
        dispatch(setToastMessage({ msg: 'API Key generated successfully', color: 'success' }));
        setGeneratedApiKey(res.data);
        // Update Redux state with the newly generated API key
        const newApiKeyData = {
          apiKey: res.data,
          name: apikey_name,
          createdDate: 'just now'
        };
        setExistingApiKey(newApiKeyData);
        dispatch(setSecuseekGenerateApiKeyDialog({ existingData: newApiKeyData }));
        setStep(2);
      }
    } catch (error) {
      const msg = extractErrorMsg(error);
      dispatch(setToastMessage({ msg: msg || 'Failed to retrieve API key!', color: 'error' }));
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = () => {
    if (generatedApiKey) {
      navigator.clipboard.writeText(generatedApiKey);
      dispatch(setToastMessage({ msg: 'API key copied to clipboard!', color: 'success', duration: 1500 }));
    }
  };

  useEffect(() => {
    if (secuseekGenerateApiKeyDialog?.existingData) {
      setExistingApiKey(secuseekGenerateApiKeyDialog?.existingData);
      setGeneratedApiKey(secuseekGenerateApiKeyDialog?.existingData?.apiKey);
      setStep(2);
    }
  }, [secuseekGenerateApiKeyDialog?.existingData]);

  useEffect(() => {
    if (secuseekGenerateApiKeyDialog?.existingData) return;

    const currentShow = secuseekGenerateApiKeyDialog?.show;
    const prevShow = prevShowRef.current;

    if (currentShow && !prevShow) {
      // Dialog is opening - fetch existing API key
      handleGetExistingApiKey();
    } else if (!currentShow && prevShow) {
      // Dialog is closing - reset state
      setStep(0);
      setGeneratedApiKey('');
      setExistingApiKey('');
      setapikey_name('');
    }
    // Update the ref with current value
    prevShowRef.current = currentShow;
  }, [secuseekGenerateApiKeyDialog?.show]);

  return (
    <Dialog
      open={secuseekGenerateApiKeyDialog?.show}
      onClose={() => {}}
      sx={{ '& .MuiBackdrop-root': { backgroundColor: !isBlankPage ? undefined : 'unset' }, '& .MuiPaper-root': { boxShadow: !isBlankPage ? undefined : 'unset' } }}
    >
      {step_ === 0 && (
        <Paper elevation={3} sx={{ p: { xs: '1rem', sm: '2rem', lg: '3rem', xl: '4rem' }, borderRadius: 3, minWidth: 360, maxWidth: 620, width: '100%' }}>
          <Stack direction="row" alignItems="center" justifyContent="center" gap={2}>
            <Typography variant="h5" fontWeight={700}>
              Checking your API Key...
            </Typography>
            {loading && <CircularProgress size={28} />}
            {!isBlankPage && (
              <Box onClick={() => dispatch(setSecuseekGenerateApiKeyDialog({ show: false, cannotClose: false }))} sx={{ position: 'absolute', top: 0, right: 0, cursor: 'pointer', zIndex: 999 }}>
                <IconButton size="small" sx={{ '&:hover': { opacity: 1 }, opacity: 0.5 }} aria-label={'IconX'}>
                  <IconX size="24" />
                </IconButton>
              </Box>
            )}
          </Stack>
        </Paper>
      )}
      {step_ === 1 && (
        <Box sx={{ minWidth: 400, maxWidth: 480, p: 3, borderRadius: 3 }}>
          <DialogTitle sx={{ pb: 2, textAlign: 'center' }}>
            <Stack direction="row" alignItems="center" justifyContent="center" gap={2}>
              <Typography variant="h5" fontWeight={700}>
                Generate SecuSeek API Key
              </Typography>
              <img src={icon} alt="SecuSeek Logo" height={32} style={{ objectFit: 'contain' }} />
              {!isBlankPage && (
                <Box onClick={() => dispatch(setSecuseekGenerateApiKeyDialog({ show: false, cannotClose: false }))} sx={{ position: 'absolute', top: 0, right: 0, cursor: 'pointer', zIndex: 999 }}>
                  <IconButton size="small" sx={{ '&:hover': { opacity: 1 }, opacity: 0.5 }} aria-label={'IconX'}>
                    <IconX size="24" />
                  </IconButton>
                </Box>
              )}
            </Stack>
          </DialogTitle>
          <DialogContent>
            <Stack spacing={3} sx={{ pt: 4 }}>
              <TextField label="Name" placeholder="Name" value={apikey_name} onChange={(e) => setapikey_name(e.target.value)} fullWidth variant="outlined" />
              {/* <TextField label="API Key" placeholder="Paste your SecuSeek API Key here" value={apiKey} onChange={(e) => setApiKey(e.target.value)} fullWidth variant="outlined" /> */}
              {/* <Box>
              <Typography variant="body2" color="text.secondary">
                How to get an API Key?
              </Typography>
              <Typography variant="caption" color="text.secondary">
                (
                <a href="https://secuseek.com/api-key" target="_blank" rel="noopener noreferrer">
                  https://secuseek.com/api-key
                </a>
                )<br />
                Generate and copy your API key from your SecuSeek account
              </Typography>
            </Box> */}
            </Stack>
          </DialogContent>
          <DialogActions sx={{ justifyContent: 'center', pt: 4, '& .MuiButtonBase-root': { height: '38px' } }}>
            <Button disabled={loading} variant="contained" color="success" size="large" sx={{ minWidth: 260, fontWeight: 500, fontSize: '1rem' }} onClick={handleGenerateApiKey}>
              Generate API Key
              {loading && <CircularProgress size={20} sx={{ ml: 1 }} />}
            </Button>
            {/* <Button onClick={() => dispatch(setSecuseekGenerateApiKeyDialog({ show: false, cannotClose: false }))} color="error" variant="text">
            Close
          </Button> */}
          </DialogActions>
        </Box>
      )}
      {step_ === 2 && (
        <Paper elevation={3} sx={{ p: { xs: '1rem', sm: '2rem', lg: '3rem', xl: '4rem' }, borderRadius: 3, minWidth: 360, maxWidth: 620, width: '100%' }}>
          <Typography variant="h5" fontWeight={700} align="center" mb={3}>
            {existingApiKey ? 'Your API Key' : 'Your API Key is Ready!'}
          </Typography>
          {!isBlankPage && (
            <Box onClick={() => dispatch(setSecuseekGenerateApiKeyDialog({ show: false, cannotClose: false }))} sx={{ position: 'absolute', top: 0, right: 0, cursor: 'pointer', zIndex: 999 }}>
              <IconButton size="small" sx={{ '&:hover': { opacity: 1 }, opacity: 0.5 }} aria-label={'IconX'}>
                <IconX size="24" />
              </IconButton>
            </Box>
          )}
          <Alert severity="warning" sx={{ mb: 3, bgcolor: '#fff8e1', color: '#b26a00', borderLeft: '5px solid #ffc519', '& .MuiAlert-icon': { mt: 0.5 } }}>
            <Typography fontWeight={600} mb={0.5} fontSize={15}>
              Important warning
            </Typography>
            <Typography fontSize={13}>
              Please save your API key in a secure location before closing this page.
              {/* This key will be shown to you only once. You won't be able to retrieve it again after closing this page. */}
            </Typography>
          </Alert>
          <Typography fontSize={13} mb={1} align="center">
            Below is your generated unique API key:
          </Typography>
          {/* {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )} */}
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', my: 3 }}>
              <CircularProgress size={28} />
            </Box>
          ) : (
            <Stack sx={{ position: 'relative', mt: '30px' }}>
              {existingApiKey && (
                <Typography
                  variant="body1"
                  fontStyle="italic"
                  fontWeight={200}
                  align="center"
                  mb={3}
                  color="text.secondary"
                  sx={{ position: 'absolute', top: '-12px', right: '0', fontSize: '10px', display: 'flex', alignItems: 'center', gap: 0.5 }}
                >
                  created {existingApiKey.createdDate} ago
                  <span style={{ fontSize: '12px' }}>
                    <TfiTime />
                  </span>
                </Typography>
              )}
              <TextField
                value={hideWithAsterisk(4, generatedApiKey, 4, 26) || ''}
                fullWidth
                InputProps={{
                  readOnly: true,
                  endAdornment: (
                    <InputAdornment position="end">
                      <Tooltip title="Copy to clipboard">
                        <IconButton onClick={handleCopy} edge="end" size="small" aria-label="copy api key" sx={{ color: 'success.main' }}>
                          <ContentCopyIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </InputAdornment>
                  ),
                  sx: { fontFamily: 'monospace', fontWeight: 600, fontSize: 16, backgroundColor: 'background.paper', color: 'text.primary', borderRadius: 1 },
                }}
                sx={{ mb: 2, mt: 1 }}
                variant="outlined"
                size="small"
              />
            </Stack>
          )}
          <Typography fontSize={13} my={2} align="center">
            After saving this key in a secure location, you can close this page.
          </Typography>
          {!isBlankPage && (
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', mt: '3rem' }}>
              <Button
                onClick={() => dispatch(setSecuseekGenerateApiKeyDialog({ show: false, cannotClose: false }))}
                variant="contained"
                color="success"
                sx={{ minWidth: 140, fontWeight: 600 }}
                disabled={loading || !generatedApiKey}
              >
                I've saved my key
              </Button>
            </Box>
          )}
        </Paper>
      )}
    </Dialog>
  );
};
export default SecuseekGenerateApiKeyDialog;

const hideWithAsterisk = (showFirstLength: number, text: string | undefined, showLastLength: number, asteriskForceLength: number) => {
  if (!text) return '';
  return text.slice(0, showFirstLength) + '*'.repeat(asteriskForceLength || text.length - showFirstLength - showLastLength) + text.slice(-showLastLength);
};
